import { useEffect } from 'react';
import type { ThreadRuntime } from '@assistant-ui/react';
import { loadServerThreadBranch } from '../server-threads';

/** The native local-thread adapter may skip history.load on first mount.
 * Its actual converted messages still need the server's selected parents.
 * No model dispatch or current server authority is inferred from this cache. */
export function useInitialServerBranch(
  threadId: string,
  thread: ThreadRuntime,
  enabled: boolean,
  isCurrentOwner: () => boolean,
) {
  useEffect(() => {
    if (!enabled) return;
    let restored = false;
    const restore = () => {
      if (restored || !isCurrentOwner() || thread.getState().isRunning) return;
      const branch = loadServerThreadBranch(threadId);
      if (!branch?.headId) return;
      const repository = thread.export();
      const ids = new Set(repository.messages.map(({ message }) => message.id));
      if (!ids.has(branch.headId)) return;
      // joinStrategy:none preserves the server's exact message identities.
      // Unrenderable ancestors can be skipped without flattening branches.
      const parent = (id: string | null): string | null => {
        const seen = new Set<string>();
        while (id && !ids.has(id)) {
          if (seen.has(id) || seen.size >= 2000 || !branch.parents.has(id))
            throw new Error('This chat changed. Reload it before continuing.');
          seen.add(id);
          id = branch.parents.get(id) ?? null;
        }
        return id;
      };
      if (repository.messages.some(({ message }) => !branch.parents.has(message.id))) return;
      const messages = repository.messages.map((item) => ({
        ...item,
        parentId: parent(branch.parents.get(item.message.id) ?? null),
      }));
      restored = true;
      if (
        repository.headId !== branch.headId ||
        messages.some((item, index) => item.parentId !== repository.messages[index]?.parentId)
      )
        thread.import({ messages, headId: branch.headId });
    };
    const unsubscribe = thread.subscribe(restore);
    restore();
    return unsubscribe;
  }, [threadId, thread, enabled, isCurrentOwner]);
}
